import { NavLink } from "react-router";
import { motion } from "motion/react";
import { BrainCircuit, ArrowRight, CheckCircle2 } from "lucide-react";

const TYPES = [
  {
    emoji: "🔧",
    label: "Realistic",
    color: "#F97316",
    desc: "Hands-on & practical",
  },
  {
    emoji: "🔬",
    label: "Investigative",
    color: "#3B82F6",
    desc: "Analytical & curious",
  },
  {
    emoji: "🎨",
    label: "Artistic",
    color: "#EC4899",
    desc: "Creative & expressive",
  },
  {
    emoji: "🤝",
    label: "Social",
    color: "#10B981",
    desc: "Helpful & empathetic",
  },
  {
    emoji: "🚀",
    label: "Enterprising",
    color: "#7C3AED",
    desc: "Driven & persuasive",
  },
  {
    emoji: "📋",
    label: "Conventional",
    color: "#06B6D4",
    desc: "Organised & precise",
  },
];

const BENEFITS = [
  "Find out which university fields match your personality",
  "Discover careers you might never have considered",
  "Get personalised course recommendations",
];

export function CareerTestSection() {
  return (
    <section
      className="py-24 lg:py-32 bg-[#FAFAFA] relative overflow-hidden"
      id="career-test"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-50/80 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-teal-50/60 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 mb-6">
              <BrainCircuit className="w-3.5 h-3.5 text-emerald-600" />
              <span
                className="text-[12px] text-emerald-700 uppercase tracking-widest"
                style={{ fontWeight: 700 }}
              >
                Free Career Test
              </span>
            </div>

            <h2
              className="text-[38px] lg:text-[50px] text-slate-900 tracking-tight mb-4 leading-tight"
              style={{ fontWeight: 900 }}
            >
              Not sure what to{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #10B981, #06B6D4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                study?
              </span>
            </h2>

            <p
              className="text-[16px] text-slate-500 leading-relaxed mb-8 max-w-md"
              style={{ fontWeight: 400 }}
            >
              Take the 5-minute RIASEC Career Personality Test — the world's
              most trusted career interest framework — and get matched to the
              fields, courses, and careers that fit <em>you</em>.
            </p>

            <div className="flex flex-col gap-3 mb-10">
              {BENEFITS.map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  </div>
                  <p
                    className="text-[14px] text-slate-600"
                    style={{ fontWeight: 500 }}
                  >
                    {b}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <NavLink
                to="/career-test"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[14px] shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:-translate-y-0.5 transition-all"
                style={{ fontWeight: 700 }}
              >
                Take the Free Test <ArrowRight className="w-4 h-4" />
              </NavLink>
              <div
                className="flex items-center gap-2 px-5 py-3.5 rounded-full bg-white border border-slate-200 text-slate-600 text-[13px]"
                style={{ fontWeight: 500 }}
              >
                ⏱️ Only 5 minutes · Know your potential
              </div>
            </div>
          </motion.div>

          {/* Right — RIASEC type grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {TYPES.map((type, i) => (
                <motion.div
                  key={type.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-[22px] mb-3"
                    style={{ backgroundColor: `${type.color}15` }}
                  >
                    {type.emoji}
                  </div>
                  <p
                    className="text-[13px] text-slate-900 mb-0.5"
                    style={{ fontWeight: 700 }}
                  >
                    {type.label}
                  </p>
                  <p
                    className="text-[11px] text-slate-400"
                    style={{ fontWeight: 400 }}
                  >
                    {type.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 text-center">
              <p
                className="text-[13px] text-emerald-800"
                style={{ fontWeight: 600 }}
              >
                🎓 Used by universities worldwide · Based on Holland's RIASEC
                theory
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
